// Searching the records on this list -- their number, name, description, status and every answer
// on their form -- from the view line, beside the search that reads inside their attached files.
//
// IT IS NOT A NEW SEARCH. The simple search form behind the magnifier has carried a "Search text"
// control all along, and it is the thing this box drives: the same control, the same operator, the
// same index. Ed, on the Suppliers list: "If I open the search on the Supplier PlatformTable and
// type in the Search text field, it does a full text search. I want that full text search made
// available at the place I indicated." So the control MOVED rather than being copied -- it is gone
// from the form, because the same field in two places is two places to look and two states to
// disagree.
//
// THE TWO BOXES ARE DIFFERENT QUESTIONS AND THE PLACEHOLDERS HAVE TO SAY SO. This one reads the
// RECORDS: a supplier's number, its name, its description, its status, and the answers on its
// form, which is why searching "Conditionally Approved" finds the suppliers in that state. The one
// beside it reads the TEXT INSIDE THE FILES attached to those records, which nothing else in the
// product does. A reader who confuses them concludes the corpus says nothing.
//
// WHAT IT CANNOT DO IS SAY A RECORD IS MISSING FROM THE INDEX. The index is written when a record
// is written, so a record last saved before its classification was being indexed has no row and
// cannot match -- and that is indistinguishable from no such record. That is a backfill, not a
// thing this box can fix, and until it is done this box answers for some classifications and not
// others.
import React, {useCallback, useState} from "react";
import {Box, IconButton, InputAdornment, TextField, Tooltip} from "@mui/material";
import {CloseRounded, ManageSearchOutlined, SearchRounded} from "@mui/icons-material";

import EventBus from "../../util/EventBus";
import {fullTextTopic} from "../../util/tableSearch";
import {Blue3} from "../../theme/styles";

type Props = {
    /** The list's enterprise component -- the one handed to the applet. */
    enterpriseComponent: any;
    /** What to call the records on screen, e.g. "Suppliers". */
    classificationPlural: string;
};

export default function SearchRecords({enterpriseComponent, classificationPlural}: Props) {
    const [query, setQuery] = useState("");
    // WHAT THE LIST IS CURRENTLY NARROWED BY, which is not the same as what is in the box: a
    // reader can type three more words without having asked for them yet. The button tells the two
    // apart -- see below.
    const [applied, setApplied] = useState("");

    const run = useCallback((asked: string) => {
        // EMITTED EVEN WHEN EMPTY, because clearing the box has to give the reader their list
        // back. The table reads an empty string as "drop the criterion", not as "match nothing".
        EventBus.components.emit(fullTextTopic(enterpriseComponent), asked);
        setApplied(asked);
    }, [enterpriseComponent]);

    const clear = useCallback(() => {
        setQuery("");
        run("");
    }, [run]);

    /**
     * THE BUTTON IS WHICHEVER VERB IS NEXT.
     *
     * Ed: "after I enter text into the Search field, I need a way to quick clear the search to
     * reset it. I was thinking of swapping the search icon on the right of the field to an X icon
     * that resets it."
     *
     * SO IT SWAPS ON THE APPLIED SEARCH, NOT ON THE BOX HAVING TEXT IN IT. Swapping the moment a
     * character is typed would take the search button away before the search had been run, leaving
     * Enter as the only way to ask -- a button that turns into its own opposite while you are
     * still typing the question. While the box differs from what the list is showing there is a
     * search to run, so it offers one; once they agree, the only thing left to do is undo it.
     */
    const showClear = applied !== "" && query.trim() === applied;

    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <ManageSearchOutlined sx={{fontSize: 18, color: Blue3, flex: "0 0 auto"}}/>
            <TextField value={query} size="small" variant="standard"
                       placeholder={`Search ${classificationPlural.toLowerCase()}…`}
                       onChange={(e) => {
                           setQuery(e.target.value);
                           // CLEARING IS IMMEDIATE, pressing enter is not. A reader who empties the
                           // box has asked for their list back and should not have to also press a
                           // key to get it; a reader mid-word has not asked for anything yet.
                           if (e.target.value === "") run("");
                       }}
                       onKeyDown={(e) => {
                           if (e.key === "Enter") { e.preventDefault(); run(query.trim()); }
                           // ESCAPE CLEARS TOO, because it is what a reader's hands already do in
                           // a search box and the button is one more thing to aim at.
                           if (e.key === "Escape") { e.preventDefault(); clear(); }
                       }}
                       slotProps={{input: {endAdornment: (
                           <InputAdornment position="end">
                               <Tooltip title={showClear
                                   ? "Clear the search"
                                   : `Search the ${classificationPlural.toLowerCase()} themselves`}>
                                   <span>
                                       <IconButton size="small"
                                                   onClick={() => (showClear ? clear() : run(query.trim()))}
                                                   disabled={!showClear && !query.trim()}
                                                   sx={{color: Blue3}}
                                                   aria-label={showClear ? "clear search" : "search records"}>
                                           {showClear ? <CloseRounded sx={{fontSize: 18}}/>
                                                      : <SearchRounded sx={{fontSize: 18}}/>}
                                       </IconButton>
                                   </span>
                               </Tooltip>
                           </InputAdornment>)}}}
                       sx={{width: 260, "& .MuiInputBase-input": {fontSize: 13}}}/>
        </Box>
    );
}
